import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { LevelsGridEditor } from '@/components/admin/evaluation-rubrics/levels-grid-editor'
import { useRubricLadderLayout, type LadderLayoutMode } from '@/hooks/use-rubric-ladder-layout'
import type { LevelDetail, OrdinalLevelDef } from '@/lib/rubric-publish-payload'
import { STANDARD_ORDINAL_LEVELS } from '@/lib/rubric-publish-payload'
import { Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

type CompetencyLadderEditorProps = {
  levels: OrdinalLevelDef[]
  levelDetails: Record<string, LevelDetail>
  onChangeLevels: (levels: OrdinalLevelDef[]) => void
  onChangeDetails: (details: Record<string, LevelDetail>) => void
  layoutMode?: LadderLayoutMode
  /** When true, omit outer border wrapper — for use inside criterion cards in SettingsPanel. */
  embedded?: boolean
}

function LadderLayoutToggle({
  value,
  onChange,
  disabled,
}: {
  value: LadderLayoutMode
  onChange: (mode: LadderLayoutMode) => void
  disabled?: boolean
}) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(next) => {
        if (next === 'columns' || next === 'list') onChange(next)
      }}
      disabled={disabled}
      className="justify-start"
    >
      <ToggleGroupItem value="columns" aria-label="Column layout" size="sm">
        Columns
      </ToggleGroupItem>
      <ToggleGroupItem value="list" aria-label="List layout" size="sm">
        List
      </ToggleGroupItem>
    </ToggleGroup>
  )
}

function toggleInSet<T>(current: Set<T>, value: T): Set<T> {
  const next = new Set(current)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return next
}

function LevelColumn({
  level,
  detail,
  collapsed,
  onToggleCollapse,
  onUpdateLevel,
  onUpdateDetail,
  onRemove,
}: {
  level: OrdinalLevelDef
  detail: LevelDetail
  collapsed: boolean
  onToggleCollapse: () => void
  onUpdateLevel: (patch: Partial<OrdinalLevelDef>) => void
  onUpdateDetail: (patch: Partial<LevelDetail>) => void
  onRemove: () => void
}) {
  if (collapsed) {
    return (
      <button
        type="button"
        className="flex h-full min-h-[12rem] w-10 shrink-0 cursor-pointer flex-col items-center justify-between rounded-md border border-dashed border-border/60 bg-muted/10 px-1 py-3"
        aria-label={`${level.label} level collapsed`}
        onClick={onToggleCollapse}
      >
        <span
          className="text-xs font-semibold text-muted-foreground [writing-mode:vertical-rl] rotate-180"
          aria-hidden
        >
          {level.label || level.key}
        </span>
        <span className="text-[10px] tabular-nums text-muted-foreground">{level.index}</span>
      </button>
    )
  }

  return (
    <div className="flex min-w-[11rem] max-w-[14rem] shrink-0 flex-col rounded-md border bg-background">
      <div className="flex items-center justify-between gap-1 border-b px-2 py-2">
        <button
          type="button"
          className="min-w-0 flex-1 truncate text-left text-xs font-semibold hover:underline"
          onClick={onToggleCollapse}
        >
          {level.label || level.key}
        </button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
      <div className="space-y-2 p-2">
        <Input
          value={level.label}
          onChange={(e) => onUpdateLevel({ label: e.target.value })}
          className="h-8 text-xs"
          placeholder="Level label"
        />
        <Textarea
          rows={4}
          className="min-h-[5rem] text-xs"
          value={(detail.indicators ?? []).join('\n')}
          onChange={(e) =>
            onUpdateDetail({
              indicators: e.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
            })
          }
          placeholder="Indicators (one per line)"
        />
        <Badge variant="secondary" className="text-[10px] tabular-nums">
          index {level.index}
        </Badge>
      </div>
    </div>
  )
}

function LadderColumnsEditor({
  levels,
  levelDetails,
  onChangeLevels,
  onChangeDetails,
}: Omit<CompetencyLadderEditorProps, 'layoutMode'>) {
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set<string>())

  function updateLevel(index: number, patch: Partial<OrdinalLevelDef>) {
    onChangeLevels(levels.map((level, i) => (i === index ? { ...level, ...patch } : level)))
  }

  function removeLevel(index: number) {
    const removed = levels[index]
    onChangeLevels(levels.filter((_, i) => i !== index))
    if (removed) {
      const next = { ...levelDetails }
      delete next[removed.key]
      onChangeDetails(next)
      setCollapsedKeys((prev) => {
        const nextKeys = new Set(prev)
        nextKeys.delete(removed.key)
        return nextKeys
      })
    }
  }

  function updateDetail(key: string, patch: Partial<LevelDetail>) {
    const prev = levelDetails[key] ?? {}
    onChangeDetails({ ...levelDetails, [key]: { ...prev, ...patch } })
  }

  return (
    <div className="@container">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {levels.map((level, index) => (
          <LevelColumn
            key={`${level.key}-${index}`}
            level={level}
            detail={levelDetails[level.key] ?? {}}
            collapsed={collapsedKeys.has(level.key)}
            onToggleCollapse={() =>
              setCollapsedKeys((prev) => toggleInSet(prev, level.key))
            }
            onUpdateLevel={(patch) => updateLevel(index, patch)}
            onUpdateDetail={(patch) => updateDetail(level.key, patch)}
            onRemove={() => removeLevel(index)}
          />
        ))}
      </div>
    </div>
  )
}

export function CompetencyLadderEditor({
  levels,
  levelDetails,
  onChangeLevels,
  onChangeDetails,
  layoutMode: layoutModeOverride,
  embedded = false,
}: CompetencyLadderEditorProps) {
  const ladderLayout = useRubricLadderLayout()
  const mode = layoutModeOverride ?? ladderLayout.mode

  function applyStandard() {
    onChangeLevels(STANDARD_ORDINAL_LEVELS.map((l) => ({ ...l })))
  }

  function addLevel() {
    const nextIndex = (levels[levels.length - 1]?.index ?? 0) + 1
    const key = `level_${nextIndex}`
    onChangeLevels([...levels, { key, label: `Level ${nextIndex}`, index: nextIndex }])
  }

  return (
    <div className={cn('space-y-3', !embedded && 'rounded-md border bg-muted/20 p-3')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-sm font-medium">Performance levels</Label>
        <div className="flex flex-wrap items-center gap-2">
          {ladderLayout.canToggle && !layoutModeOverride ? (
            <LadderLayoutToggle
              value={ladderLayout.preference}
              onChange={ladderLayout.setPreference}
            />
          ) : null}
          <Button type="button" variant="outline" size="sm" onClick={applyStandard}>
            5-level standard
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={addLevel}>
            <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
            Add level
          </Button>
        </div>
      </div>

      {mode === 'list' ? (
        <LevelsGridEditor
          levels={levels}
          levelDetails={levelDetails}
          onChangeLevels={onChangeLevels}
          onChangeDetails={onChangeDetails}
          hideToolbar
        />
      ) : (
        <LadderColumnsEditor
          levels={levels}
          levelDetails={levelDetails}
          onChangeLevels={onChangeLevels}
          onChangeDetails={onChangeDetails}
        />
      )}
    </div>
  )
}
